/* Legal / Credits.
 *
 * The hub: Escapade's own notice, where subscriptions are sold, other people's marks, the
 * colorsheet credits, and links to the privacy policy, the terms, and the licenses. The
 * licence notices themselves -- the SRD attribution among them, and the rules that govern
 * it -- live on /legal/licenses, in EsLicenses.js.
 *
 * CC BY 2(b)(2) licenses no trademark rights, and WotC's own stated safe
 * harbour is "compatible with fifth edition" or "5E compatible", which is the
 * phrasing the Escapade notice at the foot of this page uses. That sentence is
 * load-bearing: it is the compatibility claim, and it is not interchangeable
 * with the copyright or the trademark line beside it.
 *
 * THIS FILE USED TO CLAIM "Dungeons & Dragons" and "D&D" ARE ABSENT FROM THE
 * WHOLE APP. That stopped being true: EsHomeHero says "tabletop RPGs like
 * Pathfinder and D&D 5E" and the Rulesys dropdown (V061's rows, on the pack and
 * campaign forms) lists "D&D 5.5" and "Pathfinder 2e". Both are NOMINATIVE -- naming somebody else's product to say
 * what this one works with -- which is a different act from attribution and is
 * what the trademark section below exists to make explicit. The claim is
 * corrected rather than deleted because a reader arriving at this file needs to
 * know the marks are in the app, not be told they are not.
 *
 * THE TRADEMARK SECTION SITS AWKWARDLY BESIDE RULE 2 (EsLicenses.js: no other
 * attribution to Wizards) and a reader should know
 * why it is here anyway. Rule 2 forbids further ATTRIBUTION to Wizards --
 * language that implies endorsement or official standing. A non-affiliation
 * notice is the opposite claim: it exists to deny exactly what rule 2 is
 * guarding against. It is written as a disclaimer for that reason, and it names
 * no mark that the app does not itself display.
 *
 * NOBODY HERE IS A LAWYER. The wording below is the ordinary form of a
 * trademark acknowledgement and is not advice; it is worth a solicitor's eye
 * before launch, particularly the Wizards line.
 *
 * This page must stay PUBLIC -- it is not wrapped in RequireAuth in index.js. A
 * legal notice behind a login is not a legal notice.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { esRead } from '../../common/EsFetch';
import { API_COLORSHEET_SOURCES, NO_SALE_COUNTRIES, VIEW_LICENSES, VIEW_PRIVACY, VIEW_TERMS } from '../../common/EsConstants';
import { useEsStrings } from '../../common/EsStrings';
import EsMinis from '../../common/EsMinis';
import EsTranslationCredits from '../translate/EsTranslationCredits';
import EsPackNotices from './EsPackNotices';
import { EsEnglishGoverns } from './EsPolicy';

/**
 * Third-party marks the app puts on a screen, and who owns them.
 *
 * <p>ONLY WHAT IS ACTUALLY DISPLAYED. A trademark page listing marks the
 * product does not use is noise, and one that misses a mark the product does
 * use is the thing it was written to avoid. Every entry below is traceable to a
 * string a visitor can read:
 *
 * <ul>
 *   <li>Pathfinder, D&amp;D 5E -- EsHomeHero's one-line pitch, and the
 *       Rulesys dropdown's "D&amp;D 5.5" and "Pathfinder 2e".</li>
 *   <li>Unreal, PlayStation, Xbox, Switch, Quest, Steam Frame, Mac, Linux --
 *       EsHomeFeatures, the Unreal plugin card.</li>
 *   <li>Slack, Discord -- EsHomeFeatures, the table bot card. Discord
 *       again as the footer's social link.</li>
 * </ul>
 *
 * <p>A pair per entry so the list is data rather than nine hand-written lines
 * of JSX, which is nine chances to leave one out of the punctuation. Add to it
 * whenever a new mark reaches a screen.
 */
export const TRADEMARKS = [
  [ 'Dungeons & Dragons, D&D', 'Wizards of the Coast LLC' ],
  [ 'Pathfinder', 'Paizo Inc.' ],
  [ 'Unreal Engine', 'Epic Games, Inc.' ],
  [ 'PlayStation', 'Sony Interactive Entertainment Inc.' ],
  [ 'Xbox', 'Microsoft Corporation' ],
  [ 'Nintendo Switch', 'Nintendo Co., Ltd.' ],
  [ 'Meta Quest', 'Meta Platforms, Inc.' ],
  [ 'Steam, Steam Frame', 'Valve Corporation' ],
  [ 'Mac', 'Apple Inc.' ],
  [ 'Linux', 'Linus Torvalds' ],
  [ 'Slack', 'Salesforce, Inc.' ],
  [ 'Discord', 'Discord Inc.' ],
];

export const TRADEMARK_NOTICE =
  'The names above are the property of their respective owners and are used ' +
  'here only to say what Escapade works with. Escapade is not affiliated ' +
  'with, sponsored by, or endorsed by any of them, and no claim is made to ' +
  'any of these marks.';

/**
 * The tail of a trademark row's test id.
 *
 * <p>The same kebab rule the codex uses for tag chips, and for the same reason:
 * "Dungeons &amp; Dragons, D&amp;D" is a mark, not an identifier, and an
 * ampersand in an attribute selector is a quoting problem in a feature file
 * before it is anything else.
 *
 * @param pMark the mark as it is printed
 * @return the id tail, e.g. dungeons-dragons-d-d
 */
export function legalTestId( pMark ) {
  return String( pMark ?? '' )
    .toLowerCase()
    .replace( /[^a-z0-9]+/g, '-' )
    .replace( /^-+|-+$/g, '' );
}

/**
 * Where the SRD attribution used to stand: one line pointing at the licenses page, which
 * holds every notice. Drawn by this page and by the Crafter page's "licenses" marker alike.
 */
export function EsLegalLicenses() {
  const t = useEsStrings();
  return (
    <>
      <h4 data-testid="es-legal-licenses-heading" className="mb-3 text-lg font-bold text-heading">
        { t( 'legal-hub-licenses', 'Licenses' ) }
      </h4>
      <p data-testid="es-legal-licenses-notice" className="mb-10 text-base leading-8">
        { t( 'legal-hub-the-notices-attributions-and', 'The notices, attributions, and licenses for the open content Escapade uses are on the' ) } <Link to={ VIEW_LICENSES } className="underline decoration-dotted hover:text-accent-hover">{ t( 'legal-hub-licenses-link', 'Licenses' ) }</Link> { t( 'legal-hub-licenses-page', 'page.' ) }
      </p>
    </>
  );
}

/**
 * The privacy policy, the terms, and the licenses, as the row under the page's heading. Drawn
 * by this page, by the Crafter page's "policies" marker, and by the Crafter legal page when
 * its content carries no marker: Stripe's account settings link /privacy and /terms, and a
 * reader finds them from here, so the page cannot depend on what Studio holds.
 */
export function EsLegalPolicies() {
  const t = useEsStrings();
  return (
    <p data-testid="es-legal-policies" className="mb-10 text-center text-base leading-8">
      <span className="whitespace-nowrap"><Link to={ VIEW_PRIVACY } data-testid="es-legal-privacy-link" className="underline decoration-dotted hover:text-accent-hover">{ t( 'legal-hub-link-privacy', 'Privacy Policy' ) }</Link><span className="mx-2 opacity-60">&middot;</span></span><wbr />
      <span className="whitespace-nowrap"><Link to={ VIEW_TERMS } data-testid="es-legal-terms-link" className="underline decoration-dotted hover:text-accent-hover">{ t( 'legal-hub-link-terms', 'Terms of Service' ) }</Link><span className="mx-2 opacity-60">&middot;</span></span><wbr />
      <span className="whitespace-nowrap"><Link to={ VIEW_LICENSES } data-testid="es-legal-licenses-link" className="underline decoration-dotted hover:text-accent-hover">{ t( 'legal-hub-link-licenses', 'Licenses' ) }</Link></span>
    </p>
  );
}

/**
 * The palettes the shipped colorsheets come from, one row per sheet with its links.
 *
 * <p>esback reads them off colorsheets.json, the one list of them, and leaves out a sheet
 * with nothing outside to credit. Drawn by this page and by the Crafter page's "themes"
 * marker alike. Nothing at all while it loads or when esback does not answer: the rest
 * of a legal notice must not wait on, or fail with, a credit list.
 */
export function EsLegalThemes() {
  const t = useEsStrings();
  const [ themes, setThemes ] = useState( [] );

  useEffect( () => {
    let aLive = true;
    esRead( API_COLORSHEET_SOURCES )
      .then( ( pThemes ) => { if ( aLive && Array.isArray( pThemes ) ) setThemes( pThemes ); } )
      .catch( () => {} );
    return () => { aLive = false; };
  }, [] );

  if ( themes.length === 0 ) return null;

  return (
    <>
      <h4 data-testid="es-legal-themes-heading" className="mb-3 text-lg font-bold text-heading">
        { t( 'legal-hub-themes', 'Themes' ) }
      </h4>
      <p data-testid="es-legal-themes-notice" className="mb-3 text-base leading-8">
        { t( 'legal-hub-escapade-s-colorsheets-draw', 'Escapade\'s colorsheets draw on these palettes.' ) }
      </p>
      <ul data-testid="es-legal-themes" className="mb-6 text-base leading-8">
        { themes.map( ( pTheme ) => (
          <li key={ pTheme.key } data-testid={ 'es-legal-theme-' + legalTestId( pTheme.key ) }>
            <span className="font-semibold">{ pTheme.name }</span>
            { ' \u2014 ' }
            { pTheme.sources.map( ( pUrl, pIx ) => (
              <span key={ pUrl }>
                { pIx > 0 && ', ' }
                <a
                  href={ pUrl }
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-dotted hover:text-accent-hover"
                >
                  { pUrl }
                </a>
              </span>
            ) ) }
          </li>
        ) ) }
      </ul>
    </>
  );
}

// `settled` comes from EsCrafterPage alone: false while Crafter's answer is pending and this
// tree is about to be replaced (see there). Mounted on its own it is unset, and the attribute
// is absent, so the switch off renders exactly what it rendered before.
/**
 * What the hub owes whichever page draws it beyond the policy links (EsLegalPolicies, which
 * EsCrafterBlocks places itself): the notices makers' packs owe their sources. Crafter's legal
 * page (switchEsCrafter, EsCrafterSwitch) is editable copy and carries none, so EsCrafterPage
 * draws this after its blocks (its OWED); before, a reader with Crafter on met a hub with no
 * pack notices.
 */
export function EsLegalOwed() {
  return <EsPackNotices />;
}

export default function EsLegal( { settled } ) {
  const t = useEsStrings();
  return (
    <section data-testid="es-legal-page" data-settled={ settled } className="relative z-10 pb-4 pt-nav-room">
        <EsMinis />
      <div className="container">
        <div className="mx-auto px-4 xl:w-[800px] max-sm:break-words">
          <h3 data-testid="es-legal-heading" className="mb-3 text-center text-2xl font-bold text-heading sm:text-3xl">
            { t( 'legal-hub-legal-credits', 'Legal & Credits' ) }
          </h3>
          <p data-testid="es-legal-blurb" className="mb-10 text-center opacity-70">
            { t( 'legal-hub-what-escapade-uses-and', 'What Escapade uses, and under what terms.' ) }
          </p>
          <EsEnglishGoverns id="es-legal" />

          { /* The hub (Tim, 2026-10-14): the footer carries Legal alone, and the policy and
               the terms are reached from here. Age and Packs moved to the terms. */ }
          { /* A label never breaks inside itself, as in the footer: each link is nowrap with its
               trailing dot, and the wbr between them is where whole links move down. */ }
          <EsLegalPolicies />

          { /* ESCAPADE FIRST, LICENCES SECOND, other people's marks last.
               This page used to open with the licence: the first thing a reader
               met was somebody else's attribution, and the product whose legal
               page it is came third. Ours, then what we licensed, then what
               belongs to other people is the order the page is actually about. */ }
          <h4 data-testid="es-legal-escapade-heading" className="mb-3 text-lg font-bold text-heading">
            { t( 'legal-hub-escapade', 'Escapade' ) }
          </h4>
          <p data-testid="es-legal-escapade-notice" className="mb-10 text-base leading-8">
            { t( 'legal-hub-escapade-website-content-and', 'Escapade website, content, and source code are ©' ) } { new Date().getFullYear() } { t( 'legal-hub-by-alder-loons-unless', 'by Alder Loons unless otherwise indicated. Escapade is a trademark of Alder Loons. Escapade is an independent work and is 5E compatible.' ) }
          </p>

          <h4 data-testid="es-legal-countries-heading" className="mb-3 text-lg font-bold text-heading">
            { t( 'legal-hub-where-subscriptions-are-sold', 'Where subscriptions are sold' ) }
          </h4>
          <p data-testid="es-legal-countries-notice" className="mb-10 text-base leading-8">
            { t( 'legal-hub-subscriptions-and-donations-cannot', 'Subscriptions and donations cannot be bought from' ) } { NO_SALE_COUNTRIES.slice( 0, -1 ).join( ', ' ) }{ t( 'legal-hub-countries-and', ', and' ) } { NO_SALE_COUNTRIES.at( -1 ) }{ t( 'legal-hub-each-requires-a-resident', '. Each requires a resident tax representative, which Alder Loons does not yet have. Everything free on Escapade works there.' ) }
          </p>

          <EsLegalLicenses />
          <EsPackNotices />

          <h4 data-testid="es-legal-trademarks-heading" className="mb-3 text-lg font-bold text-heading">
            { t( 'legal-hub-trademarks', 'Trademarks' ) }
          </h4>
          <ul data-testid="es-legal-trademarks" className="mb-6 text-base leading-8">
            { TRADEMARKS.map( ( [ pMark, pOwner ] ) => (
              <li key={ pMark } data-testid={ 'es-legal-trademark-' + legalTestId( pMark ) }>
                <span className="font-semibold">{ pMark }</span>
                { ' \u2014 ' }
                { pOwner }
              </li>
            ) ) }
          </ul>
          <p data-testid="es-legal-trademarks-notice" className="mb-6 text-base leading-8">
            { t( 'legal-hub-trademarks-notice', TRADEMARK_NOTICE ) }
          </p>

          <EsLegalThemes />
          <EsTranslationCredits />
        </div>
      </div>
    </section>
  );
}
